import React from "react"
import styled, { css } from "styled-components"
import { format } from "date-fns"
import constants from "utils/constants"
import { ResponsiveContainer, PieChart, Pie, Cell, Label } from "recharts"

export default function CalendarBody({
  totalDateList,
  startDateIndex,
  endDateIndex,
  collections,
  handleClickDate,
  selectDay,
  taskRates,
  checkIsToday,
  checkIsOpenTodoList,
}) {
  const dayOfWeek = ["일", "월", "화", "수", "목", "금", "토"]
  const COLORS = ["#FFCF19", "#FFFFFF"]
  const rowNum = totalDateList.length / 7

  const CustomLabel = ({ viewBox, value, isToday, isSelect }) => {
    const { cx, cy } = viewBox
    const circleFill = isToday ? "#121212" : isSelect ? "#efefef" : "#fff"
    return (
      <>
        <Circle
          cx={cx}
          cy={cy}
          fill={circleFill}
          r="calc(100vw * (9 / 375))"
          stroke-width="3"
        ></Circle>
        <TodayDateTxtBlock x={cx} y={cy} fill={isToday ? "#fff" : "#000"}>
          <TodayDateTxt>{value}</TodayDateTxt>
        </TodayDateTxtBlock>
      </>
    )
  }

  return (
    <CalendarBodyBlock>
      <DayOfWeek>
        {dayOfWeek.map((day, index) => (
          <DayOfWeekTxt key={`day-of-week-${index}`}>{day}</DayOfWeekTxt>
        ))}
      </DayOfWeek>
      {totalDateList.length !== 0 && taskRates ? (
        <CalendarTable isOpenTodoList={checkIsOpenTodoList()}>
          {totalDateList.map((date, index) => {
            const isSelect = selectDay ? format(selectDay, "yyyy-MM-dd") === date : false
            return (
              <CalendarTableData
                key={`date-${index}`}
                isOpenTodoList={!selectDay}
                rowNum={rowNum}
                onClick={() => handleClickDate(date)}
              >
                <DateTop isOpenTodoList={!selectDay}>
                  <DateBlock isOpenTodoList={!selectDay}>
                    {Object.keys(taskRates).includes(date) && taskRates ? (
                      <ProgressBarContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              cx="50%"
                              cy="50%"
                              data={taskRates[date]}
                              fill="#000"
                              dataKey="rate"
                              outerRadius={"165%"}
                              innerRadius={"100%"}
                              startAngle={90}
                              endAngle={450}
                              stroke="none"
                              animationDuration={500}
                            >
                              {taskRates[date].map((entity, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                              <Label
                                content={
                                  <CustomLabel
                                    value={format(new Date(date), "d")}
                                    isToday={checkIsToday(date)}
                                    isSelect={isSelect}
                                  />
                                }
                                position={"center"}
                              />
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </ProgressBarContainer>
                    ) : (
                      <DateTxt
                        index={index}
                        startDateIndex={startDateIndex}
                        endDateIndex={endDateIndex}
                        isToday={checkIsToday(date)}
                        isSelect={isSelect}
                      >
                        {format(new Date(date), "d")}
                      </DateTxt>
                    )}
                  </DateBlock>
                </DateTop>
                {!selectDay ? (
                  <StickerBlock>
                    {collections.map((collection) => {
                      if (collection.date !== date) return null
                      return collection.stickers.map((sticker, index) => {
                        return (
                          <StickerIconBlock
                            key={`sticker-${index}`}
                            length={collection.stickers.length}
                          >
                            <StickerIcon
                              src={`${constants.imagePath}/sticker/${sticker.file_name}`}
                            />
                          </StickerIconBlock>
                        )
                      })
                    })}
                  </StickerBlock>
                ) : null}
              </CalendarTableData>
            )
          })}
        </CalendarTable>
      ) : null}
    </CalendarBodyBlock>
  )
}

const CalendarBodyBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 calc(100vw * (17 / 375));
  height: 100%;
`

const DayOfWeek = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: calc(100vw * (35 / 375));
`

const DayOfWeekTxt = styled.p`
  font-size: calc(100vw * (12 / 375));
  line-height: calc(100vw * (15 / 375));
  color: #252525;
  opacity: 0.5;
  text-align: center;
  width: calc(100vw * (48 / 375));
`

const CalendarTable = styled.div`
  width: 100%;
  height: 100%;
  margin-top: calc(100vw * (16 / 375));
  ${({ isOpenTodoList }) =>
    isOpenTodoList ? `border-left: 0;` : `border-left: 1px solid #F7F7F7;`}
  ${({ isOpenTodoList }) => isOpenTodoList && `align-items: center;`}
`

const CalendarTableData = styled.div`
  display: inline-block;
  width: calc(100vw * (48 / 375));
  height: ${({ rowNum }) => 100 / rowNum}%;
  text-align: center;
  border-bottom: 1px solid #f7f7f7;
  ${({ isOpenTodoList }) => isOpenTodoList && `border-right: 1px solid #F7F7F7;`}
  overflow: hidden;
`

const DateTop = styled.div`
  ${({ isOpenTodoList }) =>
    !isOpenTodoList &&
    `
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    `}
`

const DateBlock = styled.div`
  position: relative;
  width: calc(100vw * (24 / 375));
  height: calc(100vw * (24 / 375));
  border-radius: 50%;
  margin: 0 auto;
  ${({ isOpenTodoList }) => isOpenTodoList && `margin-top: calc(100vw * (7 / 375));`}
`

const ProgressBarContainer = styled.div`
  width: calc(100vw * (24 / 375));
  height: calc(100vw * (24 / 375));
`

const DateTxt = styled.p`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100vw * (15 / 375));
  height: calc(100vw * (15 / 375));
  border-radius: 50%;
  background: #fff;
  font-family: "Noto Sans", sans-serif;
  font-size: calc(100vw * (9 / 375));
  margin: auto;
  line-height: calc(100vw * (10 / 375));
  ${({ isToday }) =>
    isToday &&
    `
        color: #fff;
        background: #252525;
    `}
  ${({ isSelect, isToday }) =>
    isSelect &&
    !isToday &&
    `
        background: #EFEFEF;
    `}
    ${({ index, startDateIndex }) => index < startDateIndex && `opacity: 0.5;`}
    ${({ index, endDateIndex }) => index > endDateIndex && `opacity: 0.5;`}
`

const StickerBlock = styled.div`
  margin: 0 auto;
  margin-top: calc(100vw * (2 / 375));
`

const twoRowStickerStyle = css`
  width: calc(100vw * (16 / 375));
  height: calc(100vw * (16 / 375));
`

const threeRowStickerStyle = css`
  width: calc(100vw * (15 / 375));
  height: calc(100vw * (15 / 375));
`

const StickerIconBlock = styled.div`
  display: inline-block;
  ${({ length }) => (length > 6 ? threeRowStickerStyle : twoRowStickerStyle)}
`

const StickerIcon = styled.img`
  width: 100%;
  height: 100%;
  image-rendering: -moz-crisp-edges; /* Firefox */
  image-rendering: -o-crisp-edges; /* Opera */
  image-rendering: -webkit-optimize-contrast; /* Webkit (non-standard naming) */
  image-rendering: crisp-edges;
  -ms-interpolation-mode: nearest-neighbor; /* IE (non-standard property) */
  object-fit: contain;
`

const Circle = styled.circle`
  ${({ isSelect }) => isSelect && `fill: #000;`}
`

const TodayDateTxtBlock = styled.text`
  text-anchor: middle;
  dominant-baseline: central;
`

const TodayDateTxt = styled.tspan`
  alignment-baseline: middle;
  font-size: calc(100vw * (10 / 375));
  color: #252525;
  font-family: "Noto Sans", sans-serif;
`
